import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Video, Copy, LogOut, Plus, Link2, Bell } from "lucide-react";

function loadJitsiScript() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve();
    const s = document.createElement("script");
    s.src = "https://meet.jit.si/external_api.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Jitsi yüklenemedi"));
    document.body.appendChild(s);
  });
}

export default function VideoMeeting() {
  const { user, currentOrg } = useAuth();
  const [params, setParams] = useSearchParams();
  const room = params.get("room") || "";
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [joinCode, setJoinCode] = useState("");
  const shareLink = room ? `${window.location.origin}/video?room=${room}` : "";

  useEffect(() => {
    if (!room) return;
    let disposed = false;
    loadJitsiScript().then(() => {
      if (disposed || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      apiRef.current = new window.JitsiMeetExternalAPI("meet.jit.si", {
        roomName: room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName: user?.name || "Katılımcı" },
        configOverwrite: { prejoinPageEnabled: true, disableDeepLinking: true, startWithAudioMuted: true },
        interfaceConfigOverwrite: { MOBILE_APP_PROMO: false, SHOW_JITSI_WATERMARK: false },
      });
    }).catch(() => toast.error("Görüntülü toplantı başlatılamadı"));
    return () => {
      disposed = true;
      if (apiRef.current) { apiRef.current.dispose(); apiRef.current = null; }
    };
  }, [room, user?.name]);

  const startRoom = () => {
    const prefix = (currentOrg?.id || "x").replace(/[^a-zA-Z0-9]/g, "").slice(0, 8);
    const newRoom = `workhub-${prefix}-${Math.random().toString(36).slice(2, 8)}`;
    api.post("/video/notify", { room: newRoom, title: "Görüntülü Toplantı", add_to_calendar: true })
      .then(({ data }) => { if (data?.notified) toast.success(`Ekip bilgilendirildi (${data.notified} kişi) ve takvime eklendi`); })
      .catch(() => {});
    setParams({ room: newRoom });
  };
  const joinRoom = () => { if (joinCode.trim()) setParams({ room: joinCode.trim().replace(/\s/g, "") }); };
  const leave = () => setParams({});
  const notifyTeam = async () => {
    try {
      const { data } = await api.post("/video/notify", { room, title: "Görüntülü Toplantı", add_to_calendar: true });
      toast.success(`Ekip bilgilendirildi (${data.notified} kişi)`);
    } catch { toast.error("Bildirim gönderilemedi"); }
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareLink); toast.success("Bağlantı kopyalandı"); } catch { toast.error("Kopyalanamadı"); } };

  return (
    <div className="space-y-4" data-testid="video-page">
      <PageHeader title="Görüntülü Toplantı" subtitle="Ekibinizle anında güvenli görüntülü görüşme başlatın"
        actions={room && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={notifyTeam} data-testid="video-notify-team"><Bell className="h-4 w-4 mr-2" /> Ekibe Haber Ver</Button>
            <Button variant="outline" onClick={copyLink} data-testid="video-copy-link"><Copy className="h-4 w-4 mr-2" /> Bağlantıyı Kopyala</Button>
            <Button variant="outline" className="text-rose-600" onClick={leave} data-testid="video-leave"><LogOut className="h-4 w-4 mr-2" /> Odadan Çık</Button>
          </div>
        )}
      />

      {!room ? (
        <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
          <Card className="p-6 flex flex-col items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center"><Video className="h-6 w-6 text-primary" /></div>
            <h3 className="font-semibold">Yeni Oda Başlat</h3>
            <p className="text-sm text-muted-foreground">Anında bir görüntülü oda oluşturun ve bağlantıyı ekibinize gönderin.</p>
            <Button onClick={startRoom} data-testid="video-start-room"><Plus className="h-4 w-4 mr-2" /> Görüntülü Oda Başlat</Button>
          </Card>
          <Card className="p-6 flex flex-col items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center"><Link2 className="h-6 w-6 text-primary" /></div>
            <h3 className="font-semibold">Koda Göre Katıl</h3>
            <p className="text-sm text-muted-foreground">Size gönderilen oda kodunu girerek toplantıya katılın.</p>
            <div className="w-full space-y-2">
              <Label className="text-xs text-muted-foreground">Oda kodu</Label>
              <div className="flex gap-2">
                <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") joinRoom(); }} placeholder="workhub-..." data-testid="video-join-code" />
                <Button variant="outline" onClick={joinRoom} disabled={!joinCode.trim()} data-testid="video-join-btn">Katıl</Button>
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="px-4 py-2 border-b bg-muted/40 text-xs text-muted-foreground flex items-center gap-2">
            <Video className="h-3.5 w-3.5" /> Oda: <span className="font-mono">{room}</span>
          </div>
          <div ref={containerRef} className="w-full h-[70vh] bg-slate-900" data-testid="video-container" />
        </Card>
      )}
    </div>
  );
}
